import { hierarchy, type HierarchyNode } from '@visx/hierarchy'

import type {
  TemplateReferenceNode,
  TemplateVersion,
} from '../../types/templateModel'
import type { TreeNode } from './types'

/**
 * Find all nodes related to `reference`, recursively.
 *
 * Visited nodes are stored in `nodes`. If a node is found multiple
 * times (because it appears in separate branches), it will be reused.
 *
 * Delta should be negative if `getRelated` returns ancestors,
 * and positive otherwise.
 * This is used to determine the parent-to-child relationship.
 *
 * `TreeNode` is does not behave like a doubly-linked list, as it only
 * contains children, but the lowest-depth root can be found in `nodes`.
 *
 * Returns the `TreeNode` corresponding to the input `reference`.
 */
const findRelatedNodesRec = ({
  nodes,
  getRelated,
  delta,
  reference,
  depth,
}: {
  nodes: Record<string, TreeNode>
  reference: TemplateReferenceNode
  getRelated: (reference: TemplateReferenceNode) => TemplateReferenceNode[]
  depth: number
  delta: number
}): TreeNode => {
  let node: TreeNode

  if (reference.id in nodes) {
    node = nodes[reference.id]
  } else {
    node = {
      id: reference.id,
      depth,
      root: depth === 0,
      children: [],
      layer: reference.layer,
      block: reference.block,
    }
    nodes[reference.id] = node
  }

  for (const relatedReference of getRelated(reference)) {
    const relatedNode = findRelatedNodesRec({
      nodes,
      getRelated,
      delta,
      reference: relatedReference,
      depth: depth + delta,
    })

    if (delta < 0) {
      relatedNode.children.push(node)
    } else {
      node.children.push(relatedNode)
    }
  }

  return node
}

/**
 * Returns all ancestors and descendants from `reference` as a
 * mapping from their identifier to their corresponding `TreeNode`.
 *
 * The return will always have a node at depth 0 corresponding to the input.
 * The depth will go into the negatives for ancestors.
 */
const findRelatedNodes = (
  reference: TemplateReferenceNode,
): Record<string, TreeNode> => {
  const nodes: Record<string, TreeNode> = {}

  findRelatedNodesRec({
    nodes,
    getRelated: (reference) =>
      reference.referencedBy.map((node) => node.owningNode),
    delta: -1,
    reference,
    depth: 0,
  })
  findRelatedNodesRec({
    nodes,
    getRelated: (reference) =>
      reference.fields.flatMap((node) => node.referencedNodes),
    delta: 1,
    reference,
    depth: 0,
  })

  return nodes
}

export const buildReferenceHierarchy = (
  root: TemplateReferenceNode,
  idToTemplateVersion?: Record<string, TemplateVersion>,
): HierarchyNode<TreeNode>[] => {
  const nodes = findRelatedNodes(root)

  if (idToTemplateVersion) {
    // Apply replacement by moving children as needed.
    for (const node of Object.values(nodes)) {
      if (node.id in idToTemplateVersion) {
        const replaces = idToTemplateVersion[node.id].replaces

        if (replaces && replaces in nodes) {
          nodes[replaces].children = [node]
          nodes[replaces].isReplacement = true
        }
      }
    }
  }

  const childNodes = new Set(
    Object.values(nodes)
      .flatMap((n) => n.children)
      .map((n) => n.id),
  )

  const rootNodes = Object.values(nodes).filter((n) => !childNodes.has(n.id))

  return rootNodes.map((rootNode) => hierarchy(rootNode))
}
